import type { DateTime } from "luxon";
import { getEffectiveDayStart, isOvernightShiftStartHour, parseTimeString } from "@/utils/dateUtils";
import { type Day, DEFAULT_SHIFT_TIMES, type DepartmentLocation, type NurseFullRole, type NurseInShift } from "@/utils/models";
import type { Department } from "../AuthProvider";

function getDefaultShiftTimes(date: DateTime, shiftNo: number, chosenDepartment: Department, nurseRole: NurseFullRole | undefined, nurseJobId?: string) {
	const shiftType = chosenDepartment.shift_types[shiftNo];

	let shiftConfig = chosenDepartment.locations?.find((loc) => {
		if (!loc.location && loc.shift_type === shiftType) {
			if (nurseJobId && loc.jobId) return loc.jobId === nurseJobId;
			return loc.roles.includes(nurseRole as NurseFullRole);
		}
		return false;
	});

	if (!shiftConfig) {
		shiftConfig = chosenDepartment.locations?.find((loc) => !loc.location && loc.shift_type === shiftType);
	}

	if (!shiftConfig) {
		const [startHour, startMin] = DEFAULT_SHIFT_TIMES[shiftType].start;
		const [endHour, endMin] = DEFAULT_SHIFT_TIMES[shiftType].end;
		return {
			startTime: date.set({ hour: startHour, minute: startMin }),
			endTime: date.set({ hour: endHour, minute: endMin }),
		};
	}

	return {
		startTime: date.set({
			hour: Number(shiftConfig.shift_start.split(":")[0]),
			minute: Number(shiftConfig.shift_start.split(":")[1]),
		}),
		endTime: date.set({
			hour: Number(shiftConfig.shift_end.split(":")[0]),
			minute: Number(shiftConfig.shift_end.split(":")[1]),
		}),
	};
}

function getAllLocationDefaultTimes(nurseRole: NurseFullRole | undefined, chosenDepartment: Department, nurseJobId?: string) {
	const roleLocations = chosenDepartment.locations?.filter((loc) => {
		if (nurseJobId && loc.jobId) return loc.jobId === nurseJobId;
		return loc.roles.includes(nurseRole as NurseFullRole) || loc.roles.includes("all");
	});
	const defaultTimes = roleLocations?.map((loc) => {
		return [loc.shift_start, loc.shift_end];
	});

	return defaultTimes || [];
}

export function calculateShiftTimes({
	day,
	shiftNo,
	nurseRole,
	chosenDepartment,
	location,
	section,
	nurseJobId,
}: {
	day: Pick<Day, "date">;
	shiftNo: number;
	nurseRole: NurseFullRole | undefined;
	chosenDepartment: Department;
	location?: string;
	section?: string;
	nurseJobId?: string;
}) {
	const date = day.date;
	const weekday = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"][date.weekday % 7];
	const shiftType = chosenDepartment.shift_types[shiftNo];

	const matchesRoleOrJob = (loc: DepartmentLocation): boolean => {
		if (nurseJobId && loc.jobId) return loc.jobId === nurseJobId;
		return loc.roles.includes(nurseRole as NurseFullRole);
	};

	// Find matching location configuration if one exists
	let locationConfig = chosenDepartment.locations?.find(
		(loc) =>
			((!location && loc.location == "") || loc.location === location) &&
			((!section && loc.section == "") || loc.section === section) &&
			loc.shift_type === shiftType &&
			matchesRoleOrJob(loc) &&
			loc.days.includes(weekday),
	);

	if (!locationConfig) {
		locationConfig = chosenDepartment.locations?.find(
			(loc) =>
				((!location && loc.location == "") || loc.location === location) && loc.shift_type === shiftType && matchesRoleOrJob(loc) && loc.days.includes(weekday),
		);
	}

	let startTime: DateTime;
	let endTime: DateTime;

	if (!locationConfig) {
		const times = getDefaultShiftTimes(date, shiftNo, chosenDepartment, nurseRole);
		startTime = times.startTime;
		endTime = times.endTime;
	} else {
		const [startHour, startMinute] = locationConfig.shift_start.split(":");
		startTime = date.set({ hour: Number(startHour), minute: Number(startMinute) });

		const [endHour, endMinute] = locationConfig.shift_end.split(":");
		endTime = date.set({ hour: Number(endHour), minute: Number(endMinute) });
	}

	// Mirrors adjust_shift_times_for_day_start: the boundary decides if it moves and which way.
	const boundary = getEffectiveDayStart(chosenDepartment.dayStart, chosenDepartment.scheduled_shift_delta_minutes);
	if (isOvernightShiftStartHour(startTime, boundary)) {
		const { hour, minute } = parseTimeString(boundary);
		const isAmDayStart = hour * 60 + minute <= 12 * 60;
		// Both ends move, or an evening shift gains a day.
		const offset = { day: isAmDayStart ? 1 : -1 };
		startTime = startTime.plus(offset);
		endTime = endTime.plus(offset);
	}

	if (endTime.valueOf() < startTime.valueOf()) {
		endTime = endTime.plus({ day: 1 });
	}
	return { startTime, endTime };
}

export const isDefaultShiftTime = (nurseInShift: NurseInShift, chosenDepartment: Department): boolean => {
	return getAllLocationDefaultTimes(nurseInShift.role, chosenDepartment).some((defaultTime) => {
		if (nurseInShift.startTime?.toFormat("HH:mm") === defaultTime[0] && nurseInShift.endTime?.toFormat("HH:mm") === defaultTime[1]) {
			return true;
		}
		return false;
	});
};
